import {
  onUnreadCountChange,
  show as showIntercom,
} from "@intercom/messenger-js-sdk";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { Menu, MessageSquare } from "lucide-react";
import React, {
  type HTMLAttributes,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  Sidebar as _Sidebar,
  SidebarContent as _SidebarContent,
  SidebarFooter as _SidebarFooter,
} from "@/components/ui/sidebar";
import {
  TabsToggle,
  TabsToggleContent,
  TabsToggleList,
  TabsToggleTrigger,
} from "@/components/ui/tabs-toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Separator } from "@/components/ui/separator";
import { VIEW_TYPE, useGetView } from "@/hooks/useGetView";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";

import { SuperadminTools } from "@/features/developer-tools";
import {
  useAccountingFirmSidebarItems,
  useAccountingSidebarItems,
  useCompanySidebarItems,
} from "./sidebar-items";

type SidebarRenderProp = (props: {
  icons: boolean;
  isCollapsed: boolean;
  isHovered: boolean;
  isTransitioning: boolean;
}) => React.ReactNode;

type SidebarProps = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: SidebarRenderProp;
  className?: HTMLAttributes<HTMLDivElement>["className"];
  defaultCollapsed?: boolean;
};

function Sidebar({
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  defaultCollapsed,
}: SidebarProps) {
  const { t } = useTranslation();

  // Store user's preference in localStorage
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage(
    "side-bar-collapsed",
    false,
  );

  // When defaultCollapsed is true (e.g., invoice review screen), override localStorage
  // Otherwise, use the user's stored preference
  const isCollapsed = defaultCollapsed === true ? true : storedCollapsed;

  // Toggle only updates localStorage (user preference), not the override
  const setIsCollapsed = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const newValue =
        typeof value === "function" ? value(storedCollapsed) : value;
      setStoredCollapsed(newValue);
    },
    [storedCollapsed, setStoredCollapsed],
  );

  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [justClicked, setJustClicked] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (defaultCollapsed === true) {
      setIsHovered(false);
      setIsTransitioning(false);
      setJustClicked(false);
    }
  }, [defaultCollapsed]);

  const width = useMemo(() => {
    if (isCollapsed) {
      return "!w-[calc(var(--sidebar-width-icon)_+_1px)]";
    }
    return "w-[230px]";
  }, [isCollapsed]);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleMouseEnter = () => {
    if (isCollapsed && !justClicked) {
      setIsHovered(true);
      setIsTransitioning(false);
      onMouseEnter?.();
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed && isHovered) {
      setIsTransitioning(true);
      setIsHovered(false);
      onMouseLeave?.();
      setTimeout(() => setIsTransitioning(false), 250);
    }
  };

  const handleCloseSidebarOnItemClick = () => {
    if (isCollapsed && isHovered) {
      setJustClicked(true);
      handleMouseLeave();
      setTimeout(() => {
        setJustClicked(false);
      }, 300);
    }
  };

  const showIcons = isCollapsed && !isHovered;

  const shouldBeAbsolute = isCollapsed && (isHovered || isTransitioning);
  const shouldBeExpanded = isCollapsed && isHovered;

  const contextValue: SidebarContextType = {
    isCollapsed,
    isHovered,
    isTransitioning,
    setIsCollapsed,
    setIsHovered,
    setIsTransitioning,
    closeSidebarOnItemClick: handleCloseSidebarOnItemClick,
    toggleSidebar,
  };

  return (
    <SidebarContext.Provider value={contextValue}>
      <div className="relative">
        <_Sidebar
          collapsible="none"
          className={cn(
            "border-r transition-all duration-250 ease-out",
            width,
            isCollapsed && !isHovered && "overflow-hidden",
            shouldBeAbsolute && "absolute z-50 shadow-lg",
            shouldBeExpanded && "!w-[230px]",
            className,
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <SidebarMenuButton
                        onClick={toggleSidebar}
                        className="w-auto"
                      >
                        <Menu className="text-primary" size={18} />
                      </SidebarMenuButton>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("sidebar.toggle")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          {children({
            icons: showIcons,
            isCollapsed,
            isHovered,
            isTransitioning,
          })}
        </_Sidebar>
        {/* Invisible spacer to maintain layout when sidebar is floating */}
        {shouldBeAbsolute && (
          <div className="w-[calc(var(--sidebar-width-icon)_+_1px)]" />
        )}
      </div>
    </SidebarContext.Provider>
  );
}

const SidebarContent = ({
  items,
  icons,
}: {
  items: SidebarItem[];
  icons: boolean;
}) => {
  return (
    <_SidebarContent>
      <SidebarGroup>
        <SidebarGroupContent className="px-1.5 md:px-0">
          <SidebarMenu>
            <SidebarMenuItems items={items} icons={icons} shouldHighlight />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </_SidebarContent>
  );
};

const SidebarFooter = ({
  footerItems,
  showIcons,
  isCollapsed,
  isHovered,
}: {
  footerItems: SidebarItem[];
  showIcons: boolean;
  isCollapsed: boolean;
  isHovered: boolean;
}) => {
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);

  const handleSupportChat = () => {
    showIntercom();
  };

  useEffect(() => {
    onUnreadCountChange((unreadCount: number) => {
      setUnreadCount(unreadCount);
    });
  }, []);

  return (
    <>
      <Separator />
      <_SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSupportChat}>
              <MessageSquare className="text-primary" size={18} />
              {unreadCount > 0 && (
                <div className="absolute left-[6px] top-[6px]">
                  <div className="w-2 h-2 bg-orange-600 rounded-full animate-pulse" />
                </div>
              )}
              {(!isCollapsed || isHovered) && (
                <p className="whitespace-nowrap">{t("support")}</p>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItems
            items={footerItems}
            icons={showIcons}
            shouldHighlight={false}
          />
        </SidebarMenu>
      </_SidebarFooter>
    </>
  );
};

export default function AppSidebar({
  defaultCollapsed,
}: {
  defaultCollapsed?: boolean;
}) {
  const params = useParams({ from: "/_authenticated/$company" });
  const navigate = useNavigate();
  const view = useGetView();
  const { t } = useTranslation();
  const { role } = useTenant();
  const companySlug = params.company;

  const [tabsDelayedExpansion, setTabsDelayedExpansion] = useLocalStorage(
    "sidebar-tabs-delayed-expansion",
    false,
  );

  const handleMouseEnter = () => {
    setTimeout(() => {
      setTabsDelayedExpansion(true);
    }, 50);
  };

  const handleMouseLeave = () => {
    setTabsDelayedExpansion(false);
  };

  const {
    sidebarItems: accountingSidebarItems,
    footerItems: accountingFooterItems,
  } = useAccountingSidebarItems();
  const { sidebarItems: companySidebarItems, footerItems: companyFooterItems } =
    useCompanySidebarItems();

  return (
    <Sidebar
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      defaultCollapsed={defaultCollapsed}
    >
      {({ icons, isCollapsed, isHovered, isTransitioning }) => {
        const showIcons = icons;

        const tabsShowIcons =
          isCollapsed &&
          (!isHovered || isTransitioning || !tabsDelayedExpansion);

        const handleTabsChange = (value: string) => {
          setTimeout(() => {
            navigate({
              to:
                value === VIEW_TYPE.ACCOUNTING
                  ? `/${companySlug}/buchhaltung`
                  : `/${companySlug}/unternehmen`,
            });
          }, 50);
        };

        return (
          <TabsToggle
            defaultValue={view}
            className="flex flex-col flex-1"
            onValueChange={handleTabsChange}
          >
            <div className="px-2">
              {role !== CompanyRole.CLIENT && (
                <TabsToggleList className="w-full bg-sidebar-accent">
                  <TabsToggleTrigger
                    value={VIEW_TYPE.ACCOUNTING}
                    className={cn(
                      "w-full transition-all duration-250 ease-out relative overflow-hidden h-7 flex items-center justify-center",
                      tabsShowIcons && "data-[state=inactive]:hidden",
                    )}
                  >
                    <span className="transition-all duration-250 ease-out">
                      {tabsShowIcons
                        ? t("sidebar.views.accounting.label")[0]
                        : t("sidebar.views.accounting.label")}
                    </span>
                  </TabsToggleTrigger>
                  <TabsToggleTrigger
                    value={VIEW_TYPE.COMPANY}
                    className={cn(
                      "w-full transition-all duration-250 ease-out relative overflow-hidden h-7 flex items-center justify-center", // Synchronized duration and easing
                      tabsShowIcons && "data-[state=inactive]:hidden",
                    )}
                  >
                    <span className="transition-all duration-250 ease-out">
                      {tabsShowIcons
                        ? t("sidebar.views.company.label")[0]
                        : t("sidebar.views.company.label")}
                    </span>
                  </TabsToggleTrigger>
                </TabsToggleList>
              )}
            </div>
            <div className="flex flex-row flex-1 w-full relative">
              <TabsToggleContent
                value={VIEW_TYPE.ACCOUNTING}
                className="flex flex-col flex-1 m-0 transition-all duration-250 ease-out data-[state=inactive]:opacity-0 data-[state=inactive]:pointer-events-none data-[state=active]:opacity-100 data-[state=inactive]:absolute data-[state=active]:relative" // Synchronized duration and easing
              >
                <div className="flex flex-col flex-1">
                  <SidebarContent
                    items={accountingSidebarItems}
                    icons={showIcons}
                  />
                  <SidebarFooter
                    footerItems={accountingFooterItems}
                    showIcons={showIcons}
                    isCollapsed={isCollapsed}
                    isHovered={isHovered}
                  />
                </div>
              </TabsToggleContent>
              <TabsToggleContent
                value={VIEW_TYPE.COMPANY}
                className="flex flex-col flex-1 m-0 transition-all duration-250 ease-out data-[state=inactive]:opacity-0 data-[state=inactive]:pointer-events-none data-[state=active]:opacity-100 data-[state=inactive]:absolute data-[state=active]:relative"
              >
                <div className="flex flex-col flex-1">
                  <SidebarContent
                    items={companySidebarItems}
                    icons={showIcons}
                  />
                  <SidebarFooter
                    footerItems={companyFooterItems}
                    showIcons={showIcons}
                    isCollapsed={isCollapsed}
                    isHovered={isHovered}
                  />
                </div>
              </TabsToggleContent>
            </div>
          </TabsToggle>
        );
      }}
    </Sidebar>
  );
}

export const AccountingFirmSidebar = ({
  defaultCollapsed,
}: {
  defaultCollapsed?: boolean;
}) => {
  const { sidebarItems, footerItems } = useAccountingFirmSidebarItems();

  return (
    <Sidebar className="dark" defaultCollapsed={defaultCollapsed}>
      {({ icons, isCollapsed, isHovered }) => (
        <>
          <SidebarContent items={sidebarItems} icons={icons} />
          <SidebarFooter
            footerItems={footerItems}
            showIcons={icons}
            isCollapsed={isCollapsed}
            isHovered={isHovered}
          />
        </>
      )}
    </Sidebar>
  );
};

const isPathActive = (currentPath: string, itemPath: string): boolean => {
  const cleanItemPath = itemPath.split("?")[0];
  const cleanCurrentPath = currentPath.split("?")[0];

  if (cleanItemPath === "/") {
    return cleanCurrentPath === "/";
  }

  if (cleanCurrentPath.startsWith(cleanItemPath)) {
    const remainingPath = cleanCurrentPath.slice(cleanItemPath.length);
    return remainingPath === "" || remainingPath.startsWith("/");
  }

  return false;
};

type SidebarContextType = {
  isCollapsed: boolean;
  isHovered: boolean;
  isTransitioning: boolean;
  setIsCollapsed: (value: boolean | ((prev: boolean) => boolean)) => void;
  setIsHovered: (value: boolean | ((prev: boolean) => boolean)) => void;
  setIsTransitioning: (value: boolean | ((prev: boolean) => boolean)) => void;
  closeSidebarOnItemClick: () => void;
  toggleSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
};

type SidebarItem = {
  text: string;
  icon?: React.ElementType;
  path?: string;
  as?: string;
  badge?: React.ElementType;
  component?: React.ReactNode;
  submenu?: {
    text: string;
    icon: React.ElementType;
    path: string;
    as?: string;
    badge?: React.ElementType;
  }[];
};

const SidebarMenuItemContent = React.memo(
  ({
    item,
    icons,
    shouldHighlight,
    onItemClick,
  }: {
    item: SidebarItem;
    icons: boolean;
    shouldHighlight: boolean;
    onItemClick: () => void;
  }) => {
    const location = useLocation();

    const renderIcon = (icon?: React.ElementType, size = 18) => {
      if (icon) {
        const Icon = icon;
        return <Icon className="text-primary" size={size} />;
      }
      return null;
    };

    if (item.component) {
      return <SidebarMenuItem>{item.component}</SidebarMenuItem>;
    }

    const isActive = item.path
      ? shouldHighlight && isPathActive(location.pathname, item.path)
      : false;

    return (
      <SidebarMenuItem>
        {item.submenu ? (
          <>
            <SidebarMenuButton
              className={cn(
                "hover:bg-transparent cursor-default",
                icons && "!w-8 !h-8 !p-2",
              )}
            >
              <div className="relative">
                {item.badge && icons && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-2 h-2 bg-orange-600/80 rounded-full" />
                  </div>
                )}
                {renderIcon(item.icon, 18)}
              </div>
              {!icons && <p className="whitespace-nowrap">{item.text}</p>}
              {!icons && (
                <div className="ml-auto">{item.badge && <item.badge />}</div>
              )}
            </SidebarMenuButton>
            <SidebarMenuSub>
              {item.submenu.map((subItem) => (
                <SidebarSubMenuItem
                  key={subItem.text}
                  subItem={subItem}
                  shouldHighlight={shouldHighlight}
                  onItemClick={onItemClick}
                />
              ))}
            </SidebarMenuSub>
          </>
        ) : item.path ? (
          <Link to={item.path} onClick={onItemClick}>
            <SidebarMenuButton
              asChild
              isActive={isActive}
              className={icons ? "!w-8 !h-8 !p-2" : undefined}
            >
              <div>
                <div className="relative">
                  {item.badge && icons && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-2 h-2 bg-orange-600/80 rounded-full" />
                    </div>
                  )}
                  {renderIcon(item.icon, 18)}
                </div>
                {!icons && <p className="whitespace-nowrap">{item.text}</p>}
                {!icons && (
                  <div className="ml-auto">{item.badge && <item.badge />}</div>
                )}
              </div>
            </SidebarMenuButton>
          </Link>
        ) : null}
      </SidebarMenuItem>
    );
  },
);
SidebarMenuItemContent.displayName = "SidebarMenuItemContent";

// Submenu item with its own active state
const SidebarSubMenuItem = React.memo(
  ({
    subItem,
    shouldHighlight,
    onItemClick,
  }: {
    subItem: {
      text: string;
      icon: React.ElementType;
      path: string;
      as?: string;
      badge?: React.ElementType;
    };
    shouldHighlight: boolean;
    onItemClick: () => void;
  }) => {
    const location = useLocation();
    const isActive =
      shouldHighlight && isPathActive(location.pathname, subItem.path);

    const renderIcon = (icon: React.ElementType, size = 16) => {
      const Icon = icon;
      return <Icon className="text-primary" size={size} />;
    };

    return (
      <SidebarMenuSubItem>
        <Link to={subItem.path} onClick={onItemClick}>
          <SidebarMenuSubButton asChild isActive={isActive} size="md">
            <div>
              {renderIcon(subItem.icon, 16)}
              <p className="whitespace-nowrap">{subItem.text}</p>
              <div className="ml-auto">
                {subItem.badge && <subItem.badge />}
              </div>
            </div>
          </SidebarMenuSubButton>
        </Link>
      </SidebarMenuSubItem>
    );
  },
);
SidebarSubMenuItem.displayName = "SidebarSubMenuItem";

// Main container - no longer uses useLocation
const SidebarMenuItems = React.memo(
  ({
    items,
    icons,
    shouldHighlight = true,
  }: {
    items: SidebarItem[];
    icons: boolean;
    shouldHighlight: boolean;
  }) => {
    const { closeSidebarOnItemClick } = useSidebarContext();

    const handleItemClick = useCallback(() => {
      closeSidebarOnItemClick();
    }, [closeSidebarOnItemClick]);

    return (
      <>
        {items.map((item, index) => (
          <SidebarMenuItemContent
            key={item.text || `custom-${index}`}
            item={item}
            icons={icons}
            shouldHighlight={shouldHighlight}
            onItemClick={handleItemClick}
          />
        ))}
      </>
    );
  },
);
SidebarMenuItems.displayName = "SidebarMenuItems";
